/**
 * The answer key of a cryptogram: its plaintext. Kept apart from the component so the eagerly
 * loaded module file never pulls the form into the bundle.
 */
import {
  foreignLetters,
  normalizeText,
  readKey,
  shapeMismatch,
  type ShapeMismatch,
} from "./cipher";
import { givenMapping } from "./progress";
import type { CryptogramDefinition, CryptogramSolution } from "./types";

export function emptySolution(_definition: CryptogramDefinition): CryptogramSolution {
  return { plaintext: "" };
}

/** Reads whatever is stored, however wrong, and keeps what it can use. */
export function solutionOf(value: unknown): CryptogramSolution {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const plaintext = (value as { plaintext?: unknown }).plaintext;
    if (typeof plaintext === "string") return { plaintext };
  }
  return { plaintext: "" };
}

export type KeyProblem =
  | { kind: "empty" }
  | { kind: "foreign"; chars: string[] }
  | { kind: "shape"; why: ShapeMismatch }
  | { kind: "ambiguous"; letters: string[] }
  | { kind: "reused"; letters: string[] }
  | { kind: "given"; letters: string[] }
  | { kind: "self"; letters: string[] };

/** What the server's `validate_solution` would object to (or warn of), for the author's eye. */
export function keyProblems(
  definition: CryptogramDefinition,
  solution: CryptogramSolution,
): KeyProblem[] {
  const plaintext = normalizeText(solution.plaintext);
  if (!plaintext.trim()) return [{ kind: "empty" }];
  const chars = Array.from(plaintext);
  const foreign = foreignLetters(plaintext);
  if (foreign.length > 0)
    return [{ kind: "foreign", chars: [...new Set(foreign.map((i) => chars[i]!))] }];
  const why = shapeMismatch(definition.ciphertext, plaintext);
  if (why) return [{ kind: "shape", why }];
  const reading = readKey(definition.ciphertext, plaintext);
  const problems: KeyProblem[] = [];
  if (reading.ambiguous.length) problems.push({ kind: "ambiguous", letters: reading.ambiguous });
  if (reading.reused.length) problems.push({ kind: "reused", letters: reading.reused });
  const contradicted = Object.entries(givenMapping(definition))
    .filter(([cipher, plain]) => reading.key[cipher] !== undefined && reading.key[cipher] !== plain)
    .map(([cipher]) => cipher);
  if (contradicted.length) problems.push({ kind: "given", letters: contradicted });
  if (problems.length) return problems;
  const self = Object.entries(reading.key)
    .filter(([cipher, plain]) => cipher === plain)
    .map(([cipher]) => cipher);
  return self.length ? [{ kind: "self", letters: self }] : [];
}
